import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useToast } from "../../contexts/ToastContext";
import {
  fetchTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from "../../store/slices/bulkEmailSlice";

export const useTemplates = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();

  const templatesState = useAppSelector(
    (state: any) => state.bulkEmail.templates
  );
  const { templates, loading: templatesLoading } = templatesState;

  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showPreBuiltModal, setShowPreBuiltModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Form states
  const [template, setTemplate] = useState({
    name: "",
    subject: "",
    content: "",
  });
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [previewTemplate, setPreviewTemplate] = useState<any>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getTemplateId = (template: any) =>
    template?.id ||
    template?._id ||
    template?.id?.$oid ||
    template?._id?.$oid ||
    template?.uuid ||
    template?._uuid ||
    "";

  // Load templates on mount
  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);

  // Handlers
  const handleCreateTemplate = async () => {
    if (
      !template.name.trim() ||
      !template.subject.trim() ||
      !template.content.trim()
    ) {
      showToast("Please fill in all template fields", "warning");
      return;
    }

    setIsLoading(true);
    try {
      await dispatch(createTemplate(template));
      setTemplate({ name: "", subject: "", content: "" });
      setShowCreateModal(false);
      showToast("Template created successfully!", "success");
    } catch (error: any) {
      showToast("Failed to create template", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditTemplate = (templateToEdit: any) => {
    setEditingTemplate({
      ...templateToEdit,
      id: getTemplateId(templateToEdit),
    });
    setTemplate({
      name: templateToEdit.name || "",
      subject: templateToEdit.subject || "",
      content: templateToEdit.content || "",
    });
    setShowEditModal(true);
  };

  const handleUpdateTemplate = async () => {
    if (
      !template.name.trim() ||
      !template.subject.trim() ||
      !template.content.trim() ||
      !editingTemplate
    ) {
      showToast("Please fill in all template fields", "warning");
      return;
    }

    setIsLoading(true);
    try {
      await dispatch(
        updateTemplate({
          id: getTemplateId(editingTemplate),
          ...template,
        })
      );
      setShowEditModal(false);
      setEditingTemplate(null);
      showToast("Template updated successfully!", "success");
    } catch (error: any) {
      showToast("Failed to update template", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTemplate = (templateToDelete: any) => {
    setDeletingTemplate({
      ...templateToDelete,
      id: getTemplateId(templateToDelete),
    });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTemplate) return;

    setIsLoading(true);
    try {
      await dispatch(deleteTemplate(getTemplateId(deletingTemplate)));
      setShowDeleteModal(false);
      setDeletingTemplate(null);
      showToast("Template deleted successfully!", "success");
    } catch (error: any) {
      showToast("Failed to delete template", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviewTemplate = (templateToPreview: any) => {
    setPreviewTemplate(templateToPreview);
    setShowPreviewModal(true);
  };

  const handleDuplicateTemplate = async (templateToDuplicate: any) => {
    const duplicatedTemplate = {
      name: `${templateToDuplicate.name} (Copy)`,
      subject: templateToDuplicate.subject,
      content: templateToDuplicate.content,
    };

    setIsLoading(true);
    try {
      await dispatch(createTemplate(duplicatedTemplate));
      showToast("Template duplicated successfully!", "success");
    } catch (error: any) {
      showToast("Failed to duplicate template", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    dispatch(fetchTemplates());
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setTemplate({ name: "", subject: "", content: "" });
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingTemplate(null);
  };

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
    setPreviewTemplate(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingTemplate(null);
  };

  return {
    // Data
    templates: templates || [],
    templatesLoading,

    // Search
    searchTerm,
    setSearchTerm,

    // Modal states
    showCreateModal,
    setShowCreateModal,
    showEditModal,
    setShowEditModal,
    showPreviewModal,
    setShowPreviewModal,
    showPreBuiltModal,
    setShowPreBuiltModal,
    showDeleteModal,
    setShowDeleteModal,
    showPreview,
    setShowPreview,

    // Form states
    template,
    setTemplate,
    editingTemplate,
    previewTemplate,
    deletingTemplate,
    isLoading,

    // Handlers
    handleCreateTemplate,
    handleEditTemplate,
    handleUpdateTemplate,
    handleDeleteTemplate,
    handleConfirmDelete,
    handlePreviewTemplate,
    handleDuplicateTemplate,
    handleRefresh,
    handleCloseCreateModal,
    handleCloseEditModal,
    handleClosePreviewModal,
    handleCloseDeleteModal,
  };
};
